import { Entry } from '/@/shared/entities/Entry'
import { PDO } from '/@/shared/entities/PDO'
import { SDO } from '/@/shared/entities/SDO'
import { extractIfExists } from '../utils'
import { Mapping } from '../entities/Mapping'
import { XMLBuilder } from 'xmlbuilder2/lib/interfaces'
import { EthercatDevice } from '../entities/EthercatDevice'
import { BaseStartupItem, Transition } from '../entities/StartupItems'
import { groupBy } from '/@/shared/utils/array'

const formatIndex = (index: string | number) => {
  return `#x${index}`
}

const createLinkElements = (
  el: XMLBuilder,
  model: Entry | PDO | Mapping | EthercatDevice,
  tagname: string = 'Link',
) => {
  model.linkedParameters.forEach((parameter) =>
    el
      .ele(tagname, {
        Index: parameter.channel,
        Simulation: parameter.sim,
        ...(parameter.gain !== 1 ? { Gain: parameter.gain } : {}),
        ...(parameter.divide !== 1 ? { Divide: parameter.divide } : {}),
        ...(parameter.gainOffset !== 0 ? { GainOffset: parameter.gainOffset } : {}),
        ...(parameter.invert ? { Invert: parameter.invert } : {}),
        ...addIncludedProperty(parameter),
      })
      .txt(parameter.data.path),
  )
}

const addVisiblePoperty = (model: any) => {
  return {
    ...(!model.isVisible ? { Visible: false } : {}),
  }
}

const addIncludedProperty = (model: any) => {
  return {
    ...(!model.isIncluded ? { Included: false } : {}),
  }
}

const createEntryElements = (el: XMLBuilder, model: SDO | PDO) => {
  model.entries.forEach((entry) => {
    const entryEl = el.ele('Entry', {
      ...extractIfExists(entry, 'name'),
      ...extractIfExists(entry, 'group'),
      ...addVisiblePoperty(entry),
      ...addIncludedProperty(entry),
    })

    createLinkElements(entryEl, entry)

    entryEl.ele('Index').txt(formatIndex(entry.index))
    entryEl.ele('SubIndex').txt(entry.subIndex.toString())
    entryEl.ele('BitLen').txt(entry.getBitLength().toString())
    entryEl.ele('DataType').txt(entry.getTypeString())

    entry?.maps.forEach((map) => {
      const mapEl = entryEl.ele('Map', {
        ...extractIfExists(map, 'name'),
        ...extractIfExists(map, 'group'),
        ...addVisiblePoperty(map),
        ...addIncludedProperty(map),
      })

      const [Index, SubIndex] = map.offset.split(':')

      createLinkElements(mapEl, map)

      mapEl.ele('Index').txt(formatIndex(Index))
      mapEl.ele('SubIndex').txt(SubIndex.toString())
      mapEl.ele('BitLen').txt(map.bitLength.toString())
      mapEl.ele('DataType').txt(map.getTypeString())
    })

    if (entry.hasGap) {
      const gap = el.ele('Entry')
      gap.ele('Index').txt('#x0000')
      gap.ele('BitLen').txt(entry.gapLength.toString())
      gap.ele('Name').txt(entry.gapName)
    }
  })
}

const createSDOElement = (parentEl: XMLBuilder, tagName: string, sdo: SDO) => {
  const sdoEl = parentEl.ele(tagName, {
    ...extractIfExists(sdo, 'name'),
    ...extractIfExists(sdo, 'group'),
    ...addIncludedProperty(sdo),
  })

  sdoEl.ele('Type').txt(sdo.type)
  createEntryElements(sdoEl, sdo)
}

const createMailboxElements = (device: EthercatDevice, mailboxEl: XMLBuilder) => {
  if (device.startups.length === 0) {
    return
  }

  const grouped = groupBy(device.startups, (startup) => startup.constructor.name)
  const attributeBlockList = ['completeAccess']

  Array.from(grouped.entries()).forEach(([type, items]: [string, any[]]) => {
    const typeElement = mailboxEl.ele(type)
    const baseFields = Object.keys(new BaseStartupItem()).concat(attributeBlockList)
    const formatters: Record<string, (value: any) => string> = {
      index: (value: any) => {
        return formatIndex(value)
      },
    }
    // Fields whose XML tag is not simply the capitalized property name
    const tagNames: Record<string, string> = {
      idn: 'IDN',
    }
    const commands = typeElement.ele('InitCmds')

    items.forEach((startup) => {
      const command = commands.ele('InitCmd', {
        ...addIncludedProperty(startup),
        ...extractIfExists(startup, 'completeAccess'),
      })

      command.ele('Transition').txt(Transition[startup.transition])
      command.ele('Comment').txt(startup.comment)
      command.ele('Timeout').txt(startup.timeout?.toString())
      command
        .ele('Data', {
          DataType: startup.data.datatype,
          BitLen: startup.data.bitLength.toString(),
        })
        .txt(startup.data.formattedValue.toString())

      Object.entries(startup)
        .filter(([key]) => !baseFields.includes(key))
        .forEach(([key, value]: [string, any]) => {
          const field = tagNames[key] ?? key.charAt(0).toUpperCase() + key.slice(1)
          let output = value ? value.toString() : ''

          if (formatters[key]) {
            output = formatters[key](value)
          }

          command.ele(field).txt(output)
        })
    })
  })
}

export {
  formatIndex,
  createLinkElements,
  createEntryElements,
  createSDOElement,
  createMailboxElements,
  addVisiblePoperty,
  addIncludedProperty,
}
